import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["withInput", "withSuggestions", "withoutInput", "withoutSuggestions"]
  static values = { popularTags: Array }

  connect() {
    this.updateWithTagButtons()
    this.updateWithoutTagButtons()
  }

  inputChangedWith() {
    this.updateWithSuggestions()
    this.updateWithTagButtons()
  }

  inputChangedWithout() {
    this.updateWithoutSuggestions()
    this.updateWithoutTagButtons()
  }

  updateWithSuggestions() {
    const currentValue = this.withInputTarget.value
    const existingTags = currentValue.split(',').map(tag => tag.trim()).filter(tag => tag)
    const lastTag = existingTags[existingTags.length - 1] || ""
    
    if (lastTag.length > 0) {
      const popularTags = Array.isArray(this.popularTagsValue) ? this.popularTagsValue : []
      const filteredTags = popularTags.filter(tag => 
        tag.toLowerCase().includes(lastTag.toLowerCase()) && 
        !existingTags.includes(tag)
      )
      this.showWithSuggestions(filteredTags)
    } else {
      this.hideWithSuggestions()
    }
  }

  updateWithoutSuggestions() {
    const currentValue = this.withoutInputTarget.value
    const existingTags = currentValue.split(',').map(tag => tag.trim()).filter(tag => tag)
    const lastTag = existingTags[existingTags.length - 1] || ""
    
    if (lastTag.length > 0) {
      const popularTags = Array.isArray(this.popularTagsValue) ? this.popularTagsValue : []
      const filteredTags = popularTags.filter(tag => 
        tag.toLowerCase().includes(lastTag.toLowerCase()) && 
        !existingTags.includes(tag)
      )
      this.showWithoutSuggestions(filteredTags)
    } else {
      this.hideWithoutSuggestions()
    }
  }

  showWithSuggestions(tags) {
    if (!this.hasWithSuggestionsTarget) {
      this.createWithSuggestionsElement()
    }
    
    if (tags.length > 0) {
      this.withSuggestionsTarget.innerHTML = tags.map(tag => 
        `<button type="button" class="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200" data-action="click->tag-filter#selectWithTag" data-tag="${tag}">${tag}</button>`
      ).join('')
      this.withSuggestionsTarget.style.display = 'block'
    } else {
      this.hideWithSuggestions()
    }
  }

  showWithoutSuggestions(tags) {
    if (!this.hasWithoutSuggestionsTarget) {
      this.createWithoutSuggestionsElement()
    }
    
    if (tags.length > 0) {
      this.withoutSuggestionsTarget.innerHTML = tags.map(tag => 
        `<button type="button" class="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200" data-action="click->tag-filter#selectWithoutTag" data-tag="${tag}">${tag}</button>`
      ).join('')
      this.withoutSuggestionsTarget.style.display = 'block'
    } else {
      this.hideWithoutSuggestions()
    }
  }

  hideWithSuggestions() {
    if (this.hasWithSuggestionsTarget) {
      this.withSuggestionsTarget.style.display = 'none'
    }
  }

  hideWithoutSuggestions() {
    if (this.hasWithoutSuggestionsTarget) {
      this.withoutSuggestionsTarget.style.display = 'none'
    }
  }

  selectWithTag(event) {
    const selectedTag = event.target.dataset.tag
    const currentValue = this.withInputTarget.value
    const existingTags = currentValue.split(',').map(tag => tag.trim()).filter(tag => tag)
    
    if (existingTags.includes(selectedTag)) {
      const updatedTags = existingTags.filter(tag => tag !== selectedTag)
      this.withInputTarget.value = updatedTags.join(', ')
    } else {
      existingTags.push(selectedTag)
      this.withInputTarget.value = existingTags.join(', ')
    }
    
    this.hideWithSuggestions()
    this.updateWithTagButtons()
    this.withInputTarget.focus()
  }

  selectWithoutTag(event) {
    const selectedTag = event.target.dataset.tag
    const currentValue = this.withoutInputTarget.value
    const existingTags = currentValue.split(',').map(tag => tag.trim()).filter(tag => tag)
    
    if (existingTags.includes(selectedTag)) {
      const updatedTags = existingTags.filter(tag => tag !== selectedTag)
      this.withoutInputTarget.value = updatedTags.join(', ')
    } else {
      existingTags.push(selectedTag)
      this.withoutInputTarget.value = existingTags.join(', ')
    }
    
    this.hideWithoutSuggestions()
    this.updateWithoutTagButtons()
    this.withoutInputTarget.focus()
  }

  updateWithTagButtons() {
    const currentValue = this.withInputTarget.value
    const existingTags = currentValue.split(',').map(tag => tag.trim()).filter(tag => tag)
    
    const tagButtons = this.element.querySelectorAll('[data-action*="selectWithTag"]')
    tagButtons.forEach(button => {
      const tagName = button.dataset.tag
      if (existingTags.includes(tagName)) {
        button.className = "inline-flex items-center px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition-colors cursor-pointer border border-blue-300"
      } else {
        button.className = "inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors cursor-pointer"
      }
    })
  }

  updateWithoutTagButtons() {
    const currentValue = this.withoutInputTarget.value
    const existingTags = currentValue.split(',').map(tag => tag.trim()).filter(tag => tag)
    
    const tagButtons = this.element.querySelectorAll('[data-action*="selectWithoutTag"]')
    tagButtons.forEach(button => {
      const tagName = button.dataset.tag
      if (existingTags.includes(tagName)) {
        button.className = "inline-flex items-center px-2 py-1 text-xs bg-red-200 text-red-800 rounded hover:bg-red-300 transition-colors cursor-pointer border border-red-300"
      } else {
        button.className = "inline-flex items-center px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors cursor-pointer"
      }
    })
  }

  createWithSuggestionsElement() {
    const suggestions = document.createElement('div')
    suggestions.className = 'mt-2 p-2 bg-white border border-gray-300 rounded-md shadow-sm space-x-1'
    suggestions.style.display = 'none'
    suggestions.setAttribute('data-tag-filter-target', 'withSuggestions')
    this.withInputTarget.parentNode.appendChild(suggestions)
  }

  createWithoutSuggestionsElement() {
    const suggestions = document.createElement('div')
    suggestions.className = 'mt-2 p-2 bg-white border border-gray-300 rounded-md shadow-sm space-x-1'
    suggestions.style.display = 'none'
    suggestions.setAttribute('data-tag-filter-target', 'withoutSuggestions')
    this.withoutInputTarget.parentNode.appendChild(suggestions)
  }
}